/**
 * @fileoverview Lightweight, environment-gated logger utility for consistent app logging
 * @created 2025-08-17
 * @lastmodified 2025-08-18
 *
 * @description
 * Provides a simple logging interface (debug, info, warn, error) gated by
 * an environment-controlled log level to reduce noisy logs and lint warnings.
 * This replaces scattered console statements with a unified, typed utility.
 *
 * Enhancements:
 * - Contextual/scoped loggers via createScope({ context, cid }) that prefix messages
 *   with a human-friendly tag: [TG][ctx:Auth][cid:abc123]
 * - Correlation ID helper: genCorrelationId()
 *
 * @env
 * - REACT_APP_LOG_LEVEL: one of 'silent' | 'error' | 'warn' | 'info' | 'debug'
 *   Defaults to 'warn' in production, 'info' otherwise.
 *
 * @usage
 * import logger, { genCorrelationId } from '../utils/logger';
 * const cid = genCorrelationId();
 * const log = logger.createScope({ context: 'Auth', cid });
 * log.info('User signed in');
 */

/**
 * Log levels in increasing verbosity order. Lower index => less verbose.
 * Use explicit readonly typing to satisfy TS-in-JS without '@type {const}'.
 * @type {readonly ['silent','error','warn','info','debug']}
 */
const LEVELS = /** @type {readonly ['silent','error','warn','info','debug']} */ ([
  'silent',
  'error',
  'warn',
  'info',
  'debug',
]);

/**
 * Resolve active log level from environment with sensible defaults.
 * @returns {'silent'|'error'|'warn'|'info'|'debug'}
 */
function getActiveLevel() {
  // Allow runtime override via localStorage for production debugging
  // Usage: window.localStorage.setItem('tgDebug', 'true') -> refresh
  if (
    typeof window !== 'undefined' &&
    window.localStorage &&
    (window.localStorage.getItem('tgDebug') === 'true' ||
      /** @type {any} */ (window)['tgDebug'] === true)
  ) {
    return 'debug';
  }

  const nodeEnv = process.env.NODE_ENV;
  const envLevel = (process.env.REACT_APP_LOG_LEVEL || '').toLowerCase();
  if (LEVELS.includes(/** @type {any} */ (envLevel))) return /** @type {any} */ (envLevel);
  // Default: be quieter in production
  return nodeEnv === 'production' ? 'warn' : 'info';
}

/**
 * Compare two log levels.
 * @param {'silent'|'error'|'warn'|'info'|'debug'} current
 * @param {'silent'|'error'|'warn'|'info'|'debug'} target
 */
function allows(current, target) {
  return LEVELS.indexOf(target) <= LEVELS.indexOf(current);
}

const activeLevel = getActiveLevel();

/**
 * Core logger implementation. Uses native console under the hood.
 * Attach a simple prefix to make logs searchable in the console.
 */
/**
 * Build a console prefix from base tag + optional context and cid
 * @param {{ context?: string, cid?: string }} opts
 */
function makePrefix(opts = {}) {
  const parts = ['[TG]'];
  if (opts.context) parts.push(`[ctx:${opts.context}]`);
  if (opts.cid) parts.push(`[cid:${opts.cid}]`);
  return parts.join('');
}

/** Simple correlation id generator for tracing */
export function genCorrelationId() {
  return `cid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

const logger = {
  /** @param {...any} args */
  debug: (...args) => {
    if (allows(activeLevel, 'debug')) console.debug('[TG]', ...args);
  },
  /** @param {...any} args */
  info: (...args) => {
    if (allows(activeLevel, 'info')) console.info('[TG]', ...args);
  },
  /** @param {...any} args */
  warn: (...args) => {
    if (allows(activeLevel, 'warn')) console.warn('[TG]', ...args);
  },
  /** @param {...any} args */
  error: (...args) => {
    if (allows(activeLevel, 'error')) console.error('[TG]', ...args);
  },
  /** Expose current level for diagnostics */
  level: activeLevel,
  /**
   * Create a scoped logger that prefixes entries with context and/or correlation id
   * @param {{ context?: string, cid?: string }} [options]
   * @returns {{ debug: Function, info: Function, warn: Function, error: Function, level: typeof activeLevel, createScope: typeof logger.createScope }}
   */
  createScope(options = {}) {
    const prefix = makePrefix(options);
    return {
      /**
       * @param {...any} args
       */
      debug: (...args) => {
        if (allows(activeLevel, 'debug')) console.debug(prefix, ...args);
      },
      /**
       * @param {...any} args
       */
      info: (...args) => {
        if (allows(activeLevel, 'info')) console.info(prefix, ...args);
      },
      /**
       * @param {...any} args
       */
      warn: (...args) => {
        if (allows(activeLevel, 'warn')) console.warn(prefix, ...args);
      },
      /**
       * @param {...any} args
       */
      error: (...args) => {
        if (allows(activeLevel, 'error')) console.error(prefix, ...args);
      },
      level: activeLevel,
      createScope: logger.createScope,
    };
  },
};

export default logger;
